// X2 — when the glossary is used, folded onto a repeating cycle.
//
// FOUR THINGS HERE LOOK LIKE POLISH AND ARE NOT.
//
//  1. **The timezone is in the subtitle, always.** Buckets are UTC and no
//     reader timezone is recorded. A dial labelled "9am" with nothing
//     qualifying it is a claim about when a reader's morning is, which is a
//     statement about people rather than about traffic.
//  2. **A flat dial is a RESULT and says so.** The backend computes
//     `peak_ratio` and a verdict precisely so this screen never has to infer
//     "no signal" from a circle. Without the sentence, an even spread is
//     indistinguishable from a broken chart.
//  3. **The minute step is drawn and refused, not hidden.** Hiding it leaves a
//     customer wondering whether the product can do it. The refusal carries
//     the measurement behind it.
//  4. **A withheld term slot draws nothing and is counted.** `null` in
//     `term_buckets` is a slot whose count was never computed: the floor is a
//     HAVING in the rollup's INSERT, so a thin bucket does not exist on disk.
//     Drawing it at zero would claim nobody opened the term there, and a ring
//     with silent holes would read as a quiet term — hence the measured-of-slots
//     line, which compares against the cycle's own slot count because the
//     withheld buckets were never written and cannot be counted.
//
// `grain` is echoed back by the API rather than trusted from the URL: a week
// cycle always answers `hour`, because quarter-hour resolution against weekday
// slots is a different question, not a finer answer to the same one.

import { Card, ChipGroup, EmptyState } from "../../ui";
import { Radial } from "../../charts";
import type { ClockCycle, ClockGrain, ClockReport } from "./api";
import { formatCount, shownOf } from "./format";
import {
  CLOCK_CYCLE_DAY,
  CLOCK_CYCLE_LABEL,
  CLOCK_CYCLE_WEEK,
  CLOCK_EMPTY_HEADLINE,
  CLOCK_EVEN,
  CLOCK_GRAIN_HOUR,
  CLOCK_GRAIN_LABEL,
  CLOCK_GRAIN_MINUTE,
  CLOCK_GRAIN_MINUTE_WHY,
  CLOCK_GRAIN_QUARTER,
  CLOCK_PEAKED,
  CLOCK_SUBTITLE,
  CLOCK_TERM_RING_LEAD,
  CLOCK_TERM_WITHHELD_WHY,
  CLOCK_TITLE,
  SESSIONS_ARE_VISITS,
} from "./metricLabels";
import "./analytics.css";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Slot labels for a cycle. Exported for the test: the day-of-week ordering is
 *  the rule (Monday first, matching `date.weekday()`), and asserting it through
 *  rendered `<text>` positions would pin the drawing instead. */
export function slotLabels(report: ClockReport): string[] {
  if (report.cycle === "week") return WEEKDAYS;
  if (report.grain === "quarter") {
    return Array.from({ length: report.slots }, (_, slot) =>
      slot % 4 === 0 ? `${slot / 4}` : "",
    );
  }
  return Array.from({ length: report.slots }, (_, slot) => `${slot}`);
}

export type ClockProps = {
  report: ClockReport;
  cycle: ClockCycle;
  grain: ClockGrain;
  onCycleChange: (next: ClockCycle) => void;
  onGrainChange: (next: ClockGrain) => void;
  /** The term whose ring is drawn, for the accessible label. */
  termLabel?: string;
};

export function Clock({
  report,
  cycle,
  grain,
  onCycleChange,
  onGrainChange,
  termLabel,
}: ClockProps) {
  const measured = report.verdict !== "not_measured";
  const labels = slotLabels(report);
  const ring = report.term_buckets.length
    ? report.term_buckets.map((bucket) => bucket.lookups)
    : undefined;

  return (
    <Card header={CLOCK_TITLE}>
      <p className="type-bodyS maisight-analytics-muted">{CLOCK_SUBTITLE}</p>

      <div className="maisight-analytics-clock-controls">
        <ChipGroup
          label={CLOCK_CYCLE_LABEL}
          value={cycle}
          onChange={(next) => onCycleChange(next as ClockCycle)}
          options={[
            { value: "day", label: CLOCK_CYCLE_DAY },
            { value: "week", label: CLOCK_CYCLE_WEEK },
          ]}
        />
        {/* Granularity is meaningless against weekday slots, so the control is
         *  withdrawn rather than left to be ignored. */}
        {cycle === "day" ? (
          <ChipGroup
            label={CLOCK_GRAIN_LABEL}
            value={grain}
            onChange={(next) => onGrainChange(next as ClockGrain)}
            options={[
              { value: "hour", label: CLOCK_GRAIN_HOUR },
              { value: "quarter", label: CLOCK_GRAIN_QUARTER },
              {
                value: "minute",
                label: CLOCK_GRAIN_MINUTE,
                disabled: true,
                why: CLOCK_GRAIN_MINUTE_WHY,
              },
            ]}
          />
        ) : null}
      </div>

      {measured ? (
        <div className="maisight-analytics-clock-body">
          <Radial
            label={
              `Look-ups by ${report.cycle === "week" ? "day of week" : "time of day, UTC"}` +
              ` over the last ${report.days} days, ${formatCount(report.lookups)} look-ups` +
              ` across ${formatCount(report.sessions)} visits` +
              (termLabel ? `, with ${termLabel} drawn as a second ring` : "")
            }
            values={report.buckets.map((bucket) => bucket.lookups)}
            slotLabels={labels}
            labelEvery={report.slots > 24 ? 4 : report.slots > 12 ? 3 : 1}
            ring={ring}
            ringLabel={termLabel}
          />
          <div className="maisight-analytics-clock-meta">
            <p className="type-bodyS">
              {report.verdict === "even" ? CLOCK_EVEN : CLOCK_PEAKED}
            </p>
            <p className="type-bodyS maisight-analytics-muted">
              {`Busiest slot carries ${report.peak_ratio?.toFixed(2)}x the average.`}
            </p>
            {/* A term was asked for and this cycle/grain cannot answer it. The
                ring is simply absent otherwise, and an absent ring under a
                term the reader just selected reads as "this term is never
                opened" — the same lie a flat line tells. The backend owns the
                sentence; the condition is never re-derived here. */}
            {!ring && report.term_id && report.term_not_measured_reason ? (
              <p
                className="type-bodyS maisight-analytics-muted"
                data-measured="no"
              >
                {report.term_not_measured_reason}
              </p>
            ) : null}
            {ring ? (
              <>
                <p className="type-bodyS">{CLOCK_TERM_RING_LEAD}</p>
                {/* A gated ring must never read as a quiet one. */}
                {report.term_slots_measured < report.slots ? (
                  <p
                    className="type-bodyS maisight-analytics-muted"
                    data-measured="no"
                  >
                    {`${CLOCK_TERM_WITHHELD_WHY} ${shownOf(
                      report.term_slots_measured,
                      report.slots,
                      "slot",
                    )}.`}
                  </p>
                ) : null}
              </>
            ) : null}
            <p className="type-bodyS maisight-analytics-muted">
              {SESSIONS_ARE_VISITS}
            </p>
          </div>
        </div>
      ) : (
        <EmptyState
          headline={CLOCK_EMPTY_HEADLINE}
          // The backend's own sentence, never a threshold re-derived here.
          body={report.not_measured_reason ?? undefined}
        />
      )}
    </Card>
  );
}
